import { useEffect, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { Label, Message, Spinner, Tooltip } from '@iwoca/orion';
import { t, Trans } from '@lingui/macro';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import { SearchableDropdown } from 'components/SearchableDropdown/SearchableDropdown';
import { CriteriaItem } from 'components/Signup/NewSignupForm/CriteriaItem/CriteriaItem';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { TCompanySearchResultDE } from 'lendingApi/lendingApi.types';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import {
  getCompanies,
  getLegalForm,
  useCompanyInput,
} from './CompanyInputDE.helpers';
import styles from './CompanyInputDE.module.css';
import { ManualCompanyInputDE } from './ManualCompanyInputDE/ManualCompanyInputDE';

export const CompanyInputDE = () => {
  const { trackEvent } = useAmplitude();
  const {
    isManualInput,
    toggleManualInput,
    denormalisedSelectedCompany,
    resetCompanyDetails,
    updateCompanyDetailsFromLookup,
    lookupError,
  } = useCompanyInput();

  const [hasLegalForm, setHasLegalForm] = useState(
    Boolean(denormalisedSelectedCompany?.legalForm),
  );
  const [isFetchingLegalForm, setIsFetchingLegalForm] = useState(false);

  const [selectedCompany, setSelectedCompany] = useState<
    TCompanySearchResultDE | undefined
  >(denormalisedSelectedCompany);

  const { checkIntroducerPermission } = useIntroducerContext();
  const isDisabled = !checkIntroducerPermission('can_create_applications');

  useEffect(() => {
    if (!isManualInput) {
      setSelectedCompany(denormalisedSelectedCompany);
      setHasLegalForm(Boolean(denormalisedSelectedCompany?.legalForm));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManualInput, denormalisedSelectedCompany?.name]);

  const handleChange = async (value: TCompanySearchResultDE) => {
    setIsFetchingLegalForm(true);
    const legalForm = await getLegalForm(value.crefo_id);
    setSelectedCompany(value);
    setHasLegalForm(Boolean(legalForm));
    updateCompanyDetailsFromLookup(value, legalForm);
    trackEvent(AMPLITUDE_EVENTS.companySearchSelected, {
      'Company name': value.name,
    });
    setIsFetchingLegalForm(false);
  };

  const handleClear = () => {
    setSelectedCompany(undefined);
    setHasLegalForm(false);
    resetCompanyDetails();
  };

  const handleToggle = () => {
    toggleManualInput();
    trackEvent(AMPLITUDE_EVENTS.companyInputToggleClicked, {
      'CTA text': isManualInput ? 'Search your company' : 'Enter manually',
    });
  };

  const renderLabel = () => (
    <div className={styles.label}>
      {isManualInput ? (
        <div className={styles.labelWithTooltip}>
          <Label htmlFor="company.name">
            <Trans>Business name</Trans>
          </Label>
          <Tooltip
            dataTestId="manual-input-tooltip"
            content={t`Enter your client’s company details here.`}
          />
        </div>
      ) : (
        <Combobox.Label>
          <Label>
            <Trans>Business name</Trans>
          </Label>
        </Combobox.Label>
      )}

      <ButtonLink
        data-testid="input-mode-toggle"
        onClick={handleToggle}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        type="button"
      >
        {isManualInput
          ? t`Go back to search your company`
          : t`Company not found?`}
      </ButtonLink>
    </div>
  );

  if (isManualInput) {
    return <ManualCompanyInputDE labelComponent={renderLabel()} />;
  }

  return (
    <div data-testid="crefo-search">
      <SearchableDropdown
        labelComponent={renderLabel()}
        searchQuery={selectedCompany?.name}
        onChange={handleChange}
        optionsGetter={(searchQuery) => getCompanies(searchQuery)}
        optionRenderer={(company) => `${company.name} (${company.city})`}
        valueRenderer={(company) => company.name}
        placeholder={t`Search...`}
        onClear={handleClear}
        disabled={isDisabled}
        aria-disabled={isDisabled}
      />
      {hasLegalForm && (
        <CriteriaItem hasPassed className={styles.confirmation}>
          <Trans>We were able to copy the legal form from the Crefo.</Trans>
        </CriteriaItem>
      )}
      {isFetchingLegalForm && <Spinner className={styles.spinner} />}

      {lookupError && (
        <Message
          className={styles.lookupError}
          message={lookupError}
          type="error"
        />
      )}
      {selectedCompany && !hasLegalForm && (
        <CriteriaItem className={styles.confirmation}>
          <p className={styles.enterManuallyText}>
            <Trans>
              It is not possible to retrieve company information via Crefo,
              please enter{' '}
              <ButtonLink
                className={styles.enterManually}
                type="button"
                onClick={handleToggle}
                disabled={isDisabled}
                aria-disabled={isDisabled}
              >
                here
              </ButtonLink>{' '}
              the data manually.
            </Trans>
          </p>
        </CriteriaItem>
      )}
    </div>
  );
};
